import axios from 'axios';
import { signalLogout } from '../auth';

// ── CSRF token — stored in module memory, populated from response headers ──────
// The backend CSRF middleware stores the token in the server-side session and
// echoes it back as the X-CSRF-Token response header on every GET request.
// We capture it here and use it for all state-changing requests.
// This avoids reading document.cookie, which is unreliable when the Secure flag
// is set on the cookie (HTTP-only environments) or when the dev proxy is in use.
let _csrfToken = '';

function getCsrfToken() { return _csrfToken; }
export function setCsrfToken(t) { if (t) _csrfToken = t; }

function captureCsrfToken(headers) {
  // Works for both axios response headers object and fetch Headers
  const t = typeof headers.get === 'function'
    ? headers.get('x-csrf-token')
    : (headers['x-csrf-token'] || headers['X-CSRF-Token'] || '');
  if (t) _csrfToken = t;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

// All requests use session cookies — withCredentials sends/receives cookies.
// In production, REACT_APP_API_URL points to the Railway backend.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  withCredentials: true,
});

// ── On 401: signal the LoginGate to show the login form ───────────────────────
api.interceptors.response.use(
  r => { captureCsrfToken(r.headers); return r; },
  err => {
    if (err.response?.status === 401) {
      signalLogout();
    }
    return Promise.reject(err);
  }
);

// ── Attach CSRF token to all state-changing axios requests ────────────────────
api.interceptors.request.use(config => {
  const method = (config.method || 'get').toUpperCase();
  if (!SAFE_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token) config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

// ── apiFetch: drop-in fetch() replacement with session cookie + CSRF support ───
// For state-changing methods: if the CSRF token is not yet known, prime it with
// a cheap GET (which causes the backend to return X-CSRF-Token in the header).
export async function apiFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const headers = { ...(options.headers || {}) };
  if (!SAFE_METHODS.has(method)) {
    if (!getCsrfToken()) {
      // Any route behind CSRF middleware returns X-CSRF-Token header on GET.
      const r = await fetch('/api/notifications/unread-count', { credentials: 'include' });
      captureCsrfToken(r.headers);
    }
    const token = getCsrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }
  const res = await fetch(url, { ...options, headers, credentials: 'include' });
  captureCsrfToken(res.headers);
  return res;
}

// ── makeSSEUrl: returns the path as-is; cookies are sent automatically ─────────
// EventSource sends cookies to the same origin automatically, so no token
// query param is needed when using session-based auth.
export function makeSSEUrl(path) {
  return path;
}

// Projects
export const getProjects     = ()          => api.get('/api/projects').then(r => r.data);
export const createProject   = (name)      => api.post('/api/projects', { name }).then(r => r.data);
export const renameProject   = (id, name)  => api.patch(`/api/projects/${id}`, { name }).then(r => r.data);
export const deleteProject   = (id)        => api.delete(`/api/projects/${id}`).then(r => r.data);
export const reorderProjects = (order)     => api.patch('/api/projects/reorder', { order }).then(r => r.data);

// Conversations
export const getConversations   = (projectId)         => api.get(`/api/projects/${projectId}/conversations`).then(r => r.data);
export const createConversation = (projectId, mode)   => api.post(`/api/projects/${projectId}/conversations`, { mode }).then(r => r.data);
export const updateConversation = (id, patch)         => api.patch(`/api/conversations/${id}`, patch).then(r => r.data);
export const deleteConversation = (id)                => api.delete(`/api/conversations/${id}`).then(r => r.data);

// Messages
export const getMessages = (conversationId) =>
  api.get(`/api/conversations/${conversationId}/messages`).then(r => r.data);

// Run
export const runPrompt = (conversationId, prompt, attachmentIds = []) =>
  api.post(`/api/conversations/${conversationId}/run`, { prompt, attachmentIds }).then(r => r.data);

export const runPromptAbortable = (conversationId, prompt, attachmentIds = [], signal) =>
  api.post(`/api/conversations/${conversationId}/run`, { prompt, attachmentIds }, { signal }).then(r => r.data);

// Project memory (legacy)
export const getMemory    = (projectId)         => api.get(`/api/projects/${projectId}/memory`).then(r => r.data);
export const updateMemory = (projectId, memory) => api.put(`/api/projects/${projectId}/memory`, memory).then(r => r.data);

// Project Brain context (new)
export const getContext    = (projectId)          => api.get(`/api/projects/${projectId}/context`).then(r => r.data);
export const updateContext = (projectId, context) => api.put(`/api/projects/${projectId}/context`, context).then(r => r.data);

// Conversation summaries
export const getSummaries = (conversationId) => api.get(`/api/projects/conversations/${conversationId}/summaries`).then(r => r.data);

// Search
export const search = (q) => api.get('/api/search', { params: { q } }).then(r => r.data);

// Jobs — project-scoped task list (project_task and wordpress_update; dev_task/self_modify live in Task Center)
export const getProjectJobs = (projectId, limit = 50) =>
  api.get('/api/jobs', { params: { project_id: projectId, type: 'project_task,wordpress_update', limit } })
    .then(r => r.data);

// Project Tasks — approval and status (chat-originated tasks)
export const approveProjectTask = (taskId) =>
  api.post(`/api/projecttasks/${taskId}/approve`).then(r => r.data);
export const cancelProjectTask  = (taskId) =>
  api.post(`/api/projecttasks/${taskId}/cancel`).then(r => r.data);

// WordPress — read
export const getWordPressStatus     = ()                  => api.get('/api/wordpress/status').then(r => r.data);
export const getWordPressPosts      = (params = {})       => api.get('/api/wordpress/posts', { params }).then(r => r.data);
export const getWordPressCategories = ()                  => api.get('/api/wordpress/categories').then(r => r.data);

// WordPress — write (always include projectId so the job row is linked to the correct project)
export const createWordPressPost  = (projectId, body) =>
  api.post('/api/wordpress/posts', { ...body, project_id: projectId }).then(r => r.data);

export const updateWordPressPost  = (projectId, postId, fields) =>
  api.post(`/api/wordpress/posts/${postId}/update`, { ...fields, project_id: projectId }).then(r => r.data);

export const approveWordPressJob  = (jobId) => api.post(`/api/wordpress/jobs/${jobId}/approve`).then(r => r.data);
export const rejectWordPressJob   = (jobId) => api.post(`/api/wordpress/jobs/${jobId}/reject`).then(r => r.data);

// Job control actions (unified across all job types)
export const jobControl = (jobId, action) =>
  api.post(`/api/jobs/${jobId}/${action}`).then(r => r.data);

// Type-aware approve/reject — mirrors TurnBlock's handleDecision routing
export function approveJobByType(jobId, type) {
  if (type === 'wordpress_update' || type === 'wordpress_media') return approveWordPressJob(jobId);
  if (type === 'dev_task')    return api.post(`/api/devtasks/${jobId}/approve`).then(r => r.data);
  if (type === 'self_modify') return api.post(`/api/self-modify/${jobId}/approve`).then(r => r.data);
  if (type === 'project_task') return api.post(`/api/projecttasks/${jobId}/approve`).then(r => r.data);
  if (type === 'brand_config') return api.post(`/api/settings/brand-config/${jobId}/approve`).then(r => r.data);
  if (type === 'save_document') return api.post(`/api/projects/save-document/${jobId}/approve`).then(r => r.data);
  return jobControl(jobId, 'approve');
}
export function rejectJobByType(jobId, type) {
  if (type === 'wordpress_update' || type === 'wordpress_media') return rejectWordPressJob(jobId);
  if (type === 'dev_task')    return api.post(`/api/devtasks/${jobId}/cancel`).then(r => r.data);
  if (type === 'self_modify') return api.post(`/api/self-modify/${jobId}/reject`).then(r => r.data);
  if (type === 'project_task') return api.post(`/api/projecttasks/${jobId}/cancel`).then(r => r.data);
  if (type === 'brand_config') return api.post(`/api/settings/brand-config/${jobId}/reject`).then(r => r.data);
  if (type === 'save_document') return api.post(`/api/projects/save-document/${jobId}/reject`).then(r => r.data);
  return jobControl(jobId, 'reject');
}

export const getAllJobs          = (params = {}) =>
  api.get('/api/jobs', { params }).then(r => r.data);
export const getRecentMediation = (limit = 20) =>
  api.get('/api/mediation/recent', { params: { limit } }).then(r => r.data);
export const getRecentAudit     = (limit = 30) =>
  api.get('/api/audit/recent', { params: { limit } }).then(r => r.data);
export const getAuditLog        = (params = {}) =>
  api.get('/api/audit', { params }).then(r => r.data);
export const getWorkflowDecisions = (params = {}) =>
  api.get('/api/audit/workflow-decisions', { params }).then(r => r.data);
export const getAdminUsers      = () =>
  api.get('/api/users').then(r => r.data);

export const rejectJob      = (jobId) => api.post(`/api/jobs/${jobId}/reject`).then(r => r.data);
export const revertJob      = (jobId) => api.post(`/api/jobs/${jobId}/revert`).then(r => r.data);
export const getTrialStatus      = ()        => api.get('/api/billing/status').then(r => r.data);
export const startCheckout       = (priceId) => api.post('/api/billing/checkout', priceId ? { price_id: priceId } : {}).then(r => r.data);
export const openBillingPortal   = ()        => api.post('/api/billing/portal').then(r => r.data);
export const triggerGrowthScan       = (projectId)           => api.post('/api/growth/scan', projectId ? { projectId } : {}).then(r => r.data);
export const getGrowthOpportunities  = (params = {})         => api.get('/api/growth/opportunities', { params }).then(r => r.data);
export const getGrowthStatus         = ()                    => api.get('/api/growth/status').then(r => r.data);
export const getUpgradeIntent     = ()                    => api.get('/api/billing/upgrade-intent').then(r => r.data);
export const confirmGrowthUpgrade = ()                    => api.post('/api/billing/confirm-upgrade').then(r => r.data);
export const verifyGrowthUpgrade  = (subscription_id)    => api.post('/api/billing/verify-upgrade', { subscription_id }).then(r => r.data);

export const getJobOutput  = (jobId) => api.get(`/api/jobs/${jobId}/output`).then(r => r.data);
export const diagnoseJob   = (jobId) => api.post(`/api/jobs/${jobId}/diagnose`).then(r => r.data);

// Repair loop
export const proposeRepair          = (jobId)      => api.post(`/api/jobs/${jobId}/repair-proposal`).then(r => r.data);
export const getRepairProposals     = (jobId)      => api.get(`/api/jobs/${jobId}/repair-proposals`).then(r => r.data);
export const approveRepairProposal  = (proposalId) => api.post(`/api/repair-proposals/${proposalId}/approve`).then(r => r.data);
export const rejectRepairProposal   = (proposalId) => api.post(`/api/repair-proposals/${proposalId}/reject`).then(r => r.data);

// Repair history — global view of all repair proposals with job context
export const getRepairHistory = (params = {}) => api.get('/api/repairs', { params }).then(r => r.data);

// Codebase consultation — Claude Code diagnosis against real source files
export const consultCodebase = (payload) => api.post('/api/consult', payload).then(r => r.data);

// Workspaces
export const getWorkspaces        = ()                  => api.get('/api/workspaces').then(r => r.data);
export const createWorkspace      = (name, description) => api.post('/api/workspaces', { name, description }).then(r => r.data);
export const updateWorkspace      = (id, patch)         => api.patch(`/api/workspaces/${id}`, patch).then(r => r.data);
export const deleteWorkspace      = (id)                => api.delete(`/api/workspaces/${id}`).then(r => r.data);
export const getWorkspaceMemory   = (id, params = {})   => api.get(`/api/workspaces/${id}/memory`, { params }).then(r => r.data);
export const createWorkspaceMemory = (id, entry)        => api.post(`/api/workspaces/${id}/memory`, entry).then(r => r.data);
export const deleteWorkspaceMemory = (wsId, memId)      => api.delete(`/api/workspaces/${wsId}/memory/${memId}`).then(r => r.data);

// Global workflow memory (scoped queries)
export const getWorkflowMemory    = (params = {})       => api.get('/api/memory', { params }).then(r => r.data);
export const createWorkflowMemory = (entry)             => api.post('/api/memory', entry).then(r => r.data);
export const deleteWorkflowMemory = (id)                => api.delete(`/api/memory/${id}`).then(r => r.data);

// Suggestions / recommendations
export const getSuggestions = (params = {}) => api.get('/api/suggestions', { params }).then(r => r.data);

// Notifications
export const getNotifications      = (params = {}) => api.get('/api/notifications', { params }).then(r => r.data);
export const getNotifUnreadCount   = ()             => api.get('/api/notifications/unread-count').then(r => r.data);
export const markAllNotifsRead     = ()             => api.post('/api/notifications/read-all').then(r => r.data);
export const markNotifRead         = (id)           => api.post(`/api/notifications/${id}/read`).then(r => r.data);

// File Cabinet
export const getCabinetItems   = (projectId, params = {}) => api.get(`/api/projects/${projectId}/cabinet`, { params }).then(r => r.data);
export const getCabinetItem    = (projectId, itemId)       => api.get(`/api/projects/${projectId}/cabinet/${itemId}`).then(r => r.data);
export const createCabinetItem = (projectId, item)         => api.post(`/api/projects/${projectId}/cabinet`, item).then(r => r.data);
export const updateCabinetItem = (projectId, itemId, patch)=> api.patch(`/api/projects/${projectId}/cabinet/${itemId}`, patch).then(r => r.data);
export const deleteCabinetItem = (projectId, itemId)       => api.delete(`/api/projects/${projectId}/cabinet/${itemId}`).then(r => r.data);

// Memory Items (user-curated layered memory — office / conference_room / global)
export const getMemoryItems      = (params = {})         => api.get('/api/memory-items', { params }).then(r => r.data);
export const createMemoryItem    = (item)                => api.post('/api/memory-items', item).then(r => r.data);
export const getMemoryItem       = (id)                  => api.get(`/api/memory-items/${id}`).then(r => r.data);
export const updateMemoryItem    = (id, patch)           => api.put(`/api/memory-items/${id}`, patch).then(r => r.data);
export const deleteMemoryItem    = (id)                  => api.delete(`/api/memory-items/${id}`).then(r => r.data);
export const archiveMemoryItem   = (id)                  => api.post(`/api/memory-items/${id}/archive`).then(r => r.data);
export const shareMemoryItem     = (id, opts = {})       => api.post(`/api/memory-items/${id}/share`, opts).then(r => r.data);
export const approveMemoryItem   = (id)                  => api.post(`/api/memory-items/${id}/approve`).then(r => r.data);
export const rejectMemoryItem    = (id)                  => api.post(`/api/memory-items/${id}/reject`).then(r => r.data);

// Project Integrations (credentials — admin/owner only)
// Values are write-once from the UI perspective: saved encrypted, displayed masked-only, never re-shown.
export const getProjectIntegration       = (projectId, provider)         => api.get(`/api/integrations/project/${projectId}/${provider}`).then(r => r.data);
export const saveProjectIntegration      = (projectId, provider, fields) => api.put(`/api/integrations/project/${projectId}/${provider}`, fields).then(r => r.data);
export const deleteProjectIntegrationKey = (projectId, provider, key)    => api.delete(`/api/integrations/project/${projectId}/${provider}/${key}`).then(r => r.data);
export const testProjectIntegration      = (projectId, provider)         => api.post(`/api/integrations/project/${projectId}/${provider}/test`).then(r => r.data);
